import { create } from 'zustand';

import { stopTracks } from '@src/helpers';

import { IAudioStream } from '@src/types';

interface IAudioStreamStore {
  // audioStreams
  audioStreams: IAudioStream[];
  appendAudio: (audioStream: IAudioStream) => void;
  updateAudio: (streamPeerId: string, audioStream: Partial<IAudioStream>, sortBy?: keyof IAudioStream) => void;
  removeAudio: (streamId: string) => void;
  resetAudioStreams: () => void;
}

const defaultAudioStreamState: Pick<IAudioStreamStore, 'audioStreams'> = {
  audioStreams: [],
};

export const useAudioStreamStore = create<IAudioStreamStore>((set) => ({
  ...defaultAudioStreamState,
  appendAudio: (audioStream) =>
    set((state) => {
      const updatedStreams = [...(state.audioStreams || [])];

      const streamIndex = updatedStreams.findIndex(({ id = '', type }) => id === audioStream.id);
      if (streamIndex > -1) {
        // Stop tracks of the existing stream before replacing
        const existingStream = updatedStreams[streamIndex].stream;
        if (existingStream) {
          stopTracks(existingStream);
        }

        updatedStreams[streamIndex] = { ...updatedStreams[streamIndex], ...audioStream };
        return { ...state, audioStreams: updatedStreams };
      }

      return { ...state, audioStreams: [...state.audioStreams, audioStream] };
    }),
  updateAudio: (streamPeerId, audioStream) =>
    set((state) => {
      const updatedStreams = [...(state.audioStreams || [])];
      const streamIndex = updatedStreams.findIndex(({ peerId = '', id }) => [peerId, id].includes(streamPeerId));
      if (streamIndex > -1) {
        // If stream is being updated (replaced or set to null)
        if (audioStream.stream !== undefined) {
          const existingVideoMeta = updatedStreams[streamIndex];
          const oldStream = existingVideoMeta.stream;
          const newStream = audioStream.stream;

          // Only stop tracks if the stream instance has actually changed
          if (oldStream && oldStream !== newStream) {
            stopTracks(oldStream);
          }
        }

        updatedStreams[streamIndex] = { ...updatedStreams[streamIndex], ...audioStream };
        return { ...state, audioStreams: updatedStreams };
      }
      return state;
    }),
  removeAudio: (streamId) =>
    set((state) => {
      state.audioStreams
        .find(({ id }) => id === streamId)
        ?.stream?.getTracks()
        ?.forEach((track) => track.stop());
      return { ...state, audioStreams: state.audioStreams.filter(({ id }) => id !== streamId) };
    }),
  resetAudioStreams: () =>
    set((state) => {
      state.audioStreams.forEach(({ stream }) => {
        if (stream) {
          stopTracks(stream);
        }
      });
      return { ...defaultAudioStreamState };
    }),
}));
